// R116 #4 (Kai — MTR tenant notes, follow-ups, money in flight): the small PURE
// helpers every tenant surface shares — the MTR → Tenants cards
// (pages/mtr/MtrTenantsTab.ts), the shared tenant panel
// (shared/components/MtrRentLedger.ts, used by the MTR page AND the Calendar's
// form sheet) and the Calendar's tenant section. No DOM, no fetch.
// The server owns the rules (lib/mtrTenantNotes.js): the rent chip, the
// follow-up tone, can_edit. These only format what it sent.
import { Formatters } from './formatters';

export const MONTH3 = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const PAY_KINDS: Array<{ key: 'rent' | 'deposit' | 'fee' | 'other'; label: string }> = [
  { key: 'rent', label: 'Rent' }, { key: 'deposit', label: 'Deposit' }, { key: 'fee', label: 'Fee' }, { key: 'other', label: 'Other' },
];
export type Tone = 'ok' | 'warn' | 'bad' | 'muted' | 'info';
export interface RentChip { tone: Tone; label: string; title: string }
export interface FollowUp { note_id?: string; date: string; from: string | null; period: string | null; tone: 'upcoming' | 'now' | 'overdue'; label: string }

export const esc = (s: unknown) => Formatters.escapeHtml(s == null ? '' : String(s));
export const money = (n: unknown) => (n == null || n === '' || !Number.isFinite(Number(n)) ? '—' : '$' + Math.round(Number(n)).toLocaleString('en-US'));
const DAY = /^(\d{4})-(\d{2})-(\d{2})/;
/** "Oct 17" */
export function md(s: unknown): string { const m = DAY.exec(String(s || '')); return m ? `${MONTH3[Number(m[2]) - 1]} ${Number(m[3])}` : '—'; }
/** "Oct 17, 2026" */
export function fmtFull(s: unknown): string { const m = DAY.exec(String(s || '')); return m ? `${MONTH3[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}` : '—'; }
/** "Oct 2026" */
export function periodLabel(p: unknown): string { const m = /^(\d{4})-(\d{2})$/.exec(String(p || '')); return m ? `${MONTH3[Number(m[2]) - 1]} ${m[1]}` : String(p || ''); }
/** "Oct 15–17" / "Sep 29 – Oct 2" / "Oct 17" */
export function windowLabel(from: string | null | undefined, to: string | null | undefined): string {
  if (!to) return '';
  if (!from || from === to) return md(to);
  if (from.slice(0, 7) === to.slice(0, 7)) return `${md(from)}–${Number(to.slice(8, 10))}`;
  return `${md(from)} – ${md(to)}`;
}
export function localToday(): string { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }
export function addDays(day: string, n: number): string { const d = new Date(day + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); }
export function addMonths(period: string, n: number): string {
  const [y, m] = period.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return d.toISOString().slice(0, 7);
}
export function initials(name: unknown): string {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return ((parts[0][0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] || '' : '')).toUpperCase();
}
export const safeUrl = (u: unknown) => { try { const x = new URL(String(u)); return x.protocol === 'https:' || x.protocol === 'http:' ? x.href : ''; } catch { return ''; } };

/** follow-up tone → pill tone */
export const followTone = (t: string | null | undefined): Tone => (t === 'overdue' ? 'bad' : t === 'now' ? 'warn' : 'muted');
export function followText(f: { label?: string; tone?: string } | null | undefined): string {
  if (!f || !f.label) return '';
  return `⏰ ${f.label}${f.tone === 'overdue' ? ' · overdue' : f.tone === 'now' ? ' · now' : ''}`;
}
/** The rent chip a server sent (payment_status.chip), with a safe fallback for an older payload. */
export function chipOf(ps: any, unsaved = false): RentChip {
  if (ps && ps.chip && ps.chip.label) return { tone: (ps.chip.tone || 'muted') as Tone, label: String(ps.chip.label), title: String(ps.chip.title || '') };
  if (unsaved) return { tone: 'muted', label: 'not tracked yet', title: 'detected from the booking — open MTR → Tenants to start tracking rent' };
  if (!ps || !ps.trackable) return { tone: 'muted', label: 'set rent', title: (ps && ps.reason) || 'set a monthly rent amount to track rent' };
  if (ps.owed_count) return { tone: 'bad', label: `OWES ${money(ps.owed_amount)} · ${ps.owed_count} mo`, title: `owes ${(ps.owed || []).map(periodLabel).join(', ')}` };
  return { tone: 'ok', label: 'current', title: 'all due rent collected' };
}
/** The worse of two tones (bad > warn > info > ok > muted) — the Calendar's OWES badge carries an open follow-up this way. */
export function worseTone(a: Tone, b: Tone): Tone {
  const rank: Record<string, number> = { bad: 4, warn: 3, info: 2, ok: 1, muted: 0 };
  return (rank[b] || 0) > (rank[a] || 0) ? b : a;
}
/** A person's display name from an email (falls back to the local part). */
export function whoFrom(people: Array<{ email: string; full_name: string }> | null | undefined, by: unknown): string {
  if (!by) return '';
  const v = String(by);
  if (v === 'dev') return 'Local dev';
  const p = (people || []).find(x => x.email.toLowerCase() === v.toLowerCase());
  return p && p.full_name && !p.full_name.includes('@') ? p.full_name : v.split('@')[0];
}
export function ago(s: unknown): string {
  const t = Date.parse(String(s || ''));
  if (!Number.isFinite(t)) return '';
  const m = (Date.now() - t) / 60000;
  if (m < 1) return 'just now';
  if (m < 60) return `${Math.floor(m)}m ago`;
  if (m < 1440) return `${Math.floor(m / 60)}h ago`;
  if (m < 10080) return `${Math.floor(m / 1440)}d ago`;
  const d = new Date(t);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: d.getFullYear() === new Date().getFullYear() ? undefined : 'numeric' });
}
export function fmtWhen(s: unknown): string {
  const t = Date.parse(String(s || ''));
  if (!Number.isFinite(t)) return '';
  const d = new Date(t);
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: d.getFullYear() === new Date().getFullYear() ? undefined : 'numeric' })}, ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
}
